import toast from 'react-hot-toast';
import styled, { css } from 'styled-components';
import { T } from '@/ui/Text/Text';
import { useIntl } from 'react-intl';
import { Flex } from '../Flex/Flex';

export enum TOASTTYPE {
  Info = 'info',
  Success = 'success',
  Danger = 'danger',
  Warn = 'warn',
  No_Connection = 'no_connection',
  Saved = 'saved',
}

export enum DURATIONS {
  Short = 4000,
  Medium = 5000,
  Long = 7000,
}
export type Params = {
  readonly title?: string;
  readonly subtitle?: string;
  readonly mode: TOASTTYPE;
  readonly time?: DURATIONS;
  readonly icon?: string;
  readonly action?: { callback: () => any; text: string };
};

type defaultmode = {
  icon: string;
  title: string;
  subtitle?: string;
};

const StyledCard = styled.div<Pick<Params, 'mode'>>`
  padding-block: 16px;
  padding-right: 24px;
  padding-left: 16px;
  border-radius: 4px;
  min-width: 350px;
  max-width: 700px;
  width: max-content;
  overflow: hidden;
  ${({ mode, theme }) => {
    switch (mode) {
      case TOASTTYPE.Success:
      case TOASTTYPE.Saved:
        return css`
          border-left: 4px solid ${theme.color('#C4FBD1')};
          background-color: ${theme.color('#F0FFF2')};
        `;
      case TOASTTYPE.Danger:
        return css`
          border-left: 4px solid ${theme.color('#F74857')};
          background-color: ${theme.color('#FFE8EA')};
        `;
      case TOASTTYPE.Warn:
      case TOASTTYPE.No_Connection:
        return css`
          border-left: 4px solid ${theme.color('#FFA653')};
          background-color: ${theme.color('#FFF9F4')};
        `;
      default:
        return css`
          border-left: 4px solid ${theme.color('#239AE7')};
          background-color: ${theme.color('#F1F9FF')};
        `;
    }
  }}
`;

export const useToast = () => {
  const intl = useIntl();

  const defaults: {
    info: defaultmode;
    saved: defaultmode;
    success: defaultmode;
    danger: defaultmode;
    warn: defaultmode;
    no_connection: defaultmode;
  } = {
    info: {
      icon: 'ⓘ',
      title: intl.formatMessage({
        defaultMessage: 'Info!',
        id: 'wFEx3+',
      }),
    },
    saved: {
      icon: '✅',
      title: intl.formatMessage({
        defaultMessage: 'Success! 🎉',
        id: 'JUR35N',
      }),
      subtitle: intl.formatMessage({
        defaultMessage: 'Your changes have been saved!',
        id: 'BNq1iK',
      }),
    },
    success: {
      icon: '✅',
      title: intl.formatMessage({
        defaultMessage: 'Success! 🎉',
        id: 'JUR35N',
      }),
    },
    danger: {
      icon: '❌',
      title: intl.formatMessage({
        defaultMessage: "Error! Something didn't work as expected.",
        id: 'XQAjhq',
      }),
      subtitle: intl.formatMessage({
        defaultMessage: 'Try again or contact your admin.',
        id: '1mxMr2',
      }),
    },
    warn: {
      icon: '⚠️',
      title: intl.formatMessage({
        defaultMessage: 'Whoops 😕, something went wrong.',
        id: 'B9TNTu',
      }),
      subtitle: intl.formatMessage({
        defaultMessage: 'Try again or restart your app.',
        id: 'hQfwh8',
      }),
    },
    no_connection: {
      icon: '⚠️',
      title: intl.formatMessage({
        defaultMessage: 'Whoops 😕, no internet connection.',
        id: 'x1+RmH',
      }),
      subtitle: intl.formatMessage({
        defaultMessage: 'Please connect to the internet to use this feature.',
        id: 'jhprB2',
      }),
    },
  };

  return ({
    mode,
    title = defaults[mode]?.title,
    time = DURATIONS.Short,
    action,
    subtitle = defaults[mode]?.subtitle,
    icon = defaults[mode]?.icon,
  }: Params) => {
    toast(
      () => (
        <StyledCard mode={mode}>
          <Flex align="center" gap={20}>
            <div style={{ fontSize: '24px' }}>{icon}</div>
            <Flex direction="column">
              <T h4>{title}</T>
              {action && (
                <a href={action.callback() as string}>{action.text}</a>
              )}
              <div>
                <T body1>{subtitle} </T>
              </div>
            </Flex>
          </Flex>
        </StyledCard>
      ),
      {
        style: {
          padding: '0px',
          borderRadius: '0px',
          boxShadow: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
        },
        duration: time,
      },
    );
  };
};
